import React, { useState, useEffect } from 'react';
import { Users, TrendingUp, Droplet, AlertCircle, ChevronRight, Activity, AlertTriangle, BarChart3 } from 'lucide-react';
import styles from './PortfolioImpact.module.sass';

const PortfolioImpactComponent = () => {
  const [expandedInsight, setExpandedInsight] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [activeCorrelationTab, setActiveCorrelationTab] = useState('normal');
  const [activeMetricReport, setActiveMetricReport] = useState('volatility');
  const [currentAssetIndex, setCurrentAssetIndex] = useState(0);
  
  // Rally correlations (bull market)
  const [rallyCorrelations] = useState({
    ETH: 0.85,
    BTC: 0.78,
    LINK: 0.72
  });
  
  // New asset being evaluated
  const [newAsset] = useState({
    symbol: '$BNB',
    name: 'BNB',
    holders: 4200000,
    whale_pct: 23.4,
    holder_growth_7d: 2.1,
    volatility_30d: 0.5,
    dex_liquidity: 289670,
    dex_traders_24h: 10,
    dex_volume_24h: 668.16,
    buy_sell_ratio: 0.77
  });

  // Existing portfolio assets for comparison
  const [portfolioAssets] = useState([
    { symbol: 'ETH', name: 'Ethereum', weight: 40 },
    { symbol: 'BTC', name: 'Bitcoin', weight: 35 },
    { symbol: 'LINK', name: 'Chainlink', weight: 25 }
  ]);

  // Holder overlap with existing portfolio
  const [holderOverlap] = useState({
    ETH: 12,
    BTC: 8,
    LINK: 15
  });

  // Correlation with existing portfolio
  const [correlations] = useState({
    ETH: 0.12,
    BTC: 0.08,
    LINK: 0.15
  });

  const [crisisCorrelations] = useState({
    ETH: -0.05,
    BTC: -0.08,
    LINK: 0.02
  });

  // Portfolio impact metrics
  const [impactMetrics] = useState({
    volatility_change: -12.3,
    sharpe_change: 0.18,
    max_drawdown_change: -2.1,
    liquidity_score_change: 45,
    diversification_ratio: 1.34
  });

  // Overlapping wallet exposure data for each asset
  const [exposureData] = useState({
    ETH: {
      totalUSD: 118.4,
      avgPosition: 1.33,
      supplyControl: 0.58,
      walletCount: 89
    },
    BTC: {
      totalUSD: 97.2,
      avgPosition: 1.09,
      supplyControl: 0.42,
      walletCount: 89
    },
    LINK: {
      totalUSD: 142.7,
      avgPosition: 1.60,
      supplyControl: 0.71,
      walletCount: 89
    }
  });

  // Cycle through assets
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAssetIndex((prev) => (prev + 1) % portfolioAssets.length);
    }, 3000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [portfolioAssets.length]);

  const currentAsset = portfolioAssets[currentAssetIndex];
  const currentExposure = exposureData[currentAsset.symbol];

  // Calculate risk assessment
  const calculateRiskAssessment = () => {
    const avgOverlap = Object.values(holderOverlap).reduce((a, b) => a + b, 0) / Object.values(holderOverlap).length;
    const avgCorrelation = Object.values(correlations).reduce((a, b) => a + b, 0) / Object.values(correlations).length;
    const avgCrisisCorr = Object.values(crisisCorrelations).reduce((a, b) => a + b, 0) / Object.values(crisisCorrelations).length;
    
    let severity = 'positive';
    let primaryMessage = '';
    let secondaryMessage = '';
    
    // Assess overlap risk
    if (avgOverlap > 60) {
      severity = 'critical';
      primaryMessage = `Critical: ${avgOverlap.toFixed(0)}% holder overlap - you're buying the same investor base`;
    }
    // Assess correlation risk
    else if (avgCorrelation > 0.7) {
      severity = 'warning';
      primaryMessage = `Warning: High correlation (${avgCorrelation.toFixed(2)}) will amplify portfolio volatility`;
    }
    // Assess whale risk
    else if (newAsset.whale_pct > 40) {
      severity = 'warning';
      primaryMessage = `Warning: ${newAsset.whale_pct}% whale concentration creates manipulation risk`;
    }
    // Positive assessment
    else {
      severity = 'positive';
      primaryMessage = `Strong diversification: Only ${avgOverlap.toFixed(0)}% holder overlap provides true portfolio diversity`;
    }
    
    // Secondary insights
    if (avgCrisisCorr < 0) {
      secondaryMessage = `Hedge benefit: Negative crisis correlation of ${avgCrisisCorr.toFixed(2)} during market stress.`;
    } else if (newAsset.buy_sell_ratio > 0.7) {
      secondaryMessage = `Market imbalance: ${(newAsset.buy_sell_ratio * 100).toFixed(0)}% buy pressure indicates overheating.`;
    } else if (newAsset.holder_growth_7d > 2) {
      secondaryMessage = `Growing adoption: ${newAsset.holder_growth_7d}% holder increase this week.`;
    } else {
      secondaryMessage = `Liquidity depth of $${(newAsset.dex_liquidity/1000).toFixed(0)}K may limit position size.`;
    }
    
    return { severity, primaryMessage, secondaryMessage, avgOverlap, avgCorrelation, avgCrisisCorr };
  };

  const assessment = calculateRiskAssessment();

  const WalletDetailModal = () => (
    <div className={styles.walletModalContent}>
      {/* Wallet Overview */}
      <div className={styles.walletOverview}>
        <div className={styles.walletInfo}>
          <span className={styles.walletModalAddress}>{modalData?.wallet}</span>
          <span className={styles.walletModalType} style={{ 
            color: modalData?.type === 'exchange' ? '#F59E0B' : 
                   modalData?.type === 'whale' ? '#B794F6' : 
                   modalData?.type === 'smart' ? '#73F6FF' : '#FFD700' 
          }}>
            {modalData?.type === 'exchange' ? 'Exchange' : 
             modalData?.type === 'whale' ? 'Whale' :
             modalData?.type === 'smart' ? 'Smart Money' : 'Market Maker'}
          </span>
        </div>
        <div className={styles.walletOverlapBadge}>
          <span className={styles.overlapBadgeLabel}>Overlap</span>
          <span className={styles.overlapBadgeValue} style={{ 
            color: modalData?.overlap > 70 ? '#F56565' : 
                   modalData?.overlap > 50 ? '#F59E0B' : '#10b981' 
          }}>
            {modalData?.overlap}%
          </span>
        </div>
      </div>

      {/* Holdings Breakdown */}
      <div className={styles.walletSection}>
        <h4 className={styles.walletSectionTitle}>Holdings in Overlapping Assets</h4>
        <div className={styles.holdingsGrid}>
          <div className={styles.holdingItem}>
            <div className={styles.holdingHeader}>
              <span className={styles.holdingAsset}>BNB</span>
              <span className={styles.holdingPercent}>4.2%</span>
            </div>
            <div className={styles.holdingDetails}>
              <span className={styles.holdingUSD}>$1.2M USD</span>
              <span className={styles.holdingTokens}>1,200,000 tokens</span>
            </div>
          </div>
          <div className={styles.holdingItem}>
            <div className={styles.holdingHeader}>
              <span className={styles.holdingAsset}>ETH</span>
              <span className={styles.holdingPercent}>3.8%</span>
            </div>
            <div className={styles.holdingDetails}>
              <span className={styles.holdingUSD}>$890K USD</span>
              <span className={styles.holdingTokens}>245 ETH</span>
            </div>
          </div>
        </div>
      </div>

      {/* Historical Behavior */}
      <div className={styles.walletSection}>
        <h4 className={styles.walletSectionTitle}>Historical Behavior</h4>
        <div className={styles.behaviorGrid}>
          <div className={styles.behaviorStat}>
            <span className={styles.behaviorLabel}>Avg Sell Time</span>
            <span className={styles.behaviorValue} style={{ color: '#F56565' }}>3.2 hours</span>
          </div>
          <div className={styles.behaviorStat}>
            <span className={styles.behaviorLabel}>Panic Sells</span>
            <span className={styles.behaviorValue}>8/10 crashes</span>
          </div>
          <div className={styles.behaviorStat}>
            <span className={styles.behaviorLabel}>Buy Back Rate</span>
            <span className={styles.behaviorValue}>23%</span>
          </div>
          <div className={styles.behaviorStat}>
            <span className={styles.behaviorLabel}>Avg Loss</span>
            <span className={styles.behaviorValue} style={{ color: '#F56565' }}>-18%</span>
          </div>
        </div>
      </div>

      {/* Transaction Pattern */}
      <div className={styles.walletSection}>
        <h4 className={styles.walletSectionTitle}>Transaction Patterns</h4>
        <div className={styles.patternChart}>
          <div className={styles.patternRow}>
            <span className={styles.patternLabel}>Market Drop 5-10%</span>
            <div className={styles.patternBar}>
              <div className={styles.patternFill} style={{ width: '30%', background: '#F59E0B' }} />
            </div>
            <span className={styles.patternPercent}>30% chance</span>
          </div>
          <div className={styles.patternRow}>
            <span className={styles.patternLabel}>Market Drop 10-20%</span>
            <div className={styles.patternBar}>
              <div className={styles.patternFill} style={{ width: '75%', background: '#F56565' }} />
            </div>
            <span className={styles.patternPercent}>75% chance</span>
          </div>
          <div className={styles.patternRow}>
            <span className={styles.patternLabel}>Market Drop 20%+</span>
            <div className={styles.patternBar}>
              <div className={styles.patternFill} style={{ width: '95%', background: '#F56565' }} />
            </div>
            <span className={styles.patternPercent}>95% chance</span>
          </div>
        </div>
      </div>

      {/* Risk Assessment */}
      <div className={styles.walletRiskAssessment}>
        <AlertTriangle size={16} style={{ color: '#F56565' }} />
        <div className={styles.riskContent}>
          <span className={styles.riskTitle}>High Cascade Risk</span>
          <span className={styles.riskDescription}>
            This wallet has historically triggered selling in 12 other wallets within 6 hours of their transactions
          </span>
        </div>
      </div>
    </div>
  );

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'critical': return <AlertTriangle className={styles.aiIcon} style={{ color: '#F56565' }} />;
      case 'warning': return <AlertCircle className={styles.aiIcon} style={{ color: '#F59E0B' }} />;
      default: return <Activity className={styles.aiIcon} style={{ color: '#10b981' }} />;
    }
  };

  const getSeverityStyle = (severity) => {
    switch(severity) {
      case 'critical': return styles.aiSummaryCritical;
      case 'warning': return styles.aiSummaryWarning;
      default: return styles.aiSummaryPositive;
    }
  };

  const getOverlapColor = (percentage) => {
    if (percentage > 50) return '#F56565';
    if (percentage > 25) return '#F59E0B';
    return '#10b981';
  };

  const getCorrelationColor = (value) => {
    if (value > 0.7) return '#F56565';
    if (value > 0.4) return '#F59E0B';
    if (value < -0.3) return '#B794F6';
    return '#10b981';
  };

  const OverlapWalletsModal = () => {
    const [selectedWallet, setSelectedWallet] = useState(null);
    
    const walletData = [
      { address: '0x742d...35Dc', type: 'whale', typeColor: '#B794F6', usdtHolding: '4.2M', usdtPercent: '0.1%', ethHolding: '245', ethPercent: '0.001%' },
      { address: 'Binance 7', type: 'exchange', typeColor: '#F59E0B', usdtHolding: '89M', usdtPercent: '2.1%', ethHolding: '12,450', ethPercent: '0.05%' },
      { address: 'Jump Trading', type: 'smart', typeColor: '#73F6FF', usdtHolding: '15M', usdtPercent: '0.36%', ethHolding: '890', ethPercent: '0.003%' },
      { address: '0x9f82...1a3C', type: 'mm', typeColor: '#FFD700', usdtHolding: '7.8M', usdtPercent: '0.19%', ethHolding: '423', ethPercent: '0.002%' },
      { address: '0xa1b2...45De', type: 'whale', typeColor: '#B794F6', usdtHolding: '2.3M', usdtPercent: '0.05%', ethHolding: '98', ethPercent: '0.0004%' },
      { address: 'Kraken 4', type: 'exchange', typeColor: '#F59E0B', usdtHolding: '45M', usdtPercent: '1.1%', ethHolding: '8,900', ethPercent: '0.04%' },
      { address: '0xc3d4...67Ef', type: 'smart', typeColor: '#73F6FF', usdtHolding: '8.9M', usdtPercent: '0.21%', ethHolding: '567', ethPercent: '0.002%' },
      { address: '0xd4e5...78Fg', type: 'whale', typeColor: '#B794F6', usdtHolding: '5.6M', usdtPercent: '0.13%', ethHolding: '312', ethPercent: '0.001%' },
      { address: 'FTX Alameda', type: 'mm', typeColor: '#FFD700', usdtHolding: '23M', usdtPercent: '0.55%', ethHolding: '1,890', ethPercent: '0.008%' },
      { address: '0xe5f6...89Gh', type: 'smart', typeColor: '#73F6FF', usdtHolding: '11M', usdtPercent: '0.26%', ethHolding: '678', ethPercent: '0.003%' },
      { address: 'Coinbase 2', type: 'exchange', typeColor: '#F59E0B', usdtHolding: '67M', usdtPercent: '1.6%', ethHolding: '9,800', ethPercent: '0.04%' },
      { address: '0xf6g7...90Hi', type: 'whale', typeColor: '#B794F6', usdtHolding: '3.4M', usdtPercent: '0.08%', ethHolding: '189', ethPercent: '0.0008%' }
    ];
    
    return (
      <div className={styles.overlapWalletsModalContent}>
        <div className={styles.overlapModalLayout}>
          {/* Left side - Wallet list */}
          <div className={styles.overlapWalletsSection}>
            <h4 className={styles.overlapListTitle}>Overlapping Holders - {modalData?.asset}</h4>
            
            <div className={styles.overlapWalletRows}>
              {walletData.map((wallet) => (
                <div 
                  key={wallet.address}
                  className={`${styles.overlapWalletRow} ${selectedWallet?.address === wallet.address ? styles.overlapWalletRowActive : ''}`}
                  onClick={() => setSelectedWallet(wallet)}
                >
                  <span className={styles.overlapWalletAddress}>{wallet.address}</span>
                  <div className={styles.overlapHoldingsCompact}>
                    <div className={styles.holdingCompactCol}>
                      <span className={styles.holdingCompactAmount}>{wallet.usdtHolding}</span>
                      <span className={styles.holdingCompactPercent}>BNB</span>
                    </div>
                    <div className={styles.holdingCompactCol}>
                      <span className={styles.holdingCompactAmount}>{wallet.ethHolding}</span>
                      <span className={styles.holdingCompactPercent}>{modalData?.asset}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Wallet details */}
          <div className={styles.walletDetailSection}>
            {selectedWallet ? (
              <>
                <div className={styles.walletDetailHeader}>
                  <span className={styles.walletDetailAddress}>{selectedWallet.address}</span>
                  <span className={styles.walletDetailType} style={{ color: selectedWallet.typeColor }}>
                    {selectedWallet.type === 'exchange' ? 'Exchange' : 
                     selectedWallet.type === 'whale' ? 'Whale' :
                     selectedWallet.type === 'smart' ? 'Smart Money' : 'Market Maker'}
                  </span>
                </div>

                {/* Holdings */}
                <div className={styles.walletDetailBlock}>
                  <h5 className={styles.walletDetailBlockTitle}>Holdings</h5>
                  <div className={styles.holdingsDetailGrid}>
                    <div className={styles.holdingDetailItem}>
                      <div className={styles.holdingDetailHeader}>
                        <span className={styles.holdingDetailAsset}>BNB</span>
                        <span className={styles.holdingDetailPercent}>{selectedWallet.usdtPercent}</span>
                      </div>
                      <span className={styles.holdingDetailUSD}>{selectedWallet.usdtHolding} USD</span>
                    </div>
                    <div className={styles.holdingDetailItem}>
                      <div className={styles.holdingDetailHeader}>
                        <span className={styles.holdingDetailAsset}>{modalData?.asset}</span>
                        <span className={styles.holdingDetailPercent}>{selectedWallet.ethPercent}</span>
                      </div>
                      <span className={styles.holdingDetailUSD}>{selectedWallet.ethHolding} tokens</span>
                    </div>
                  </div>
                </div>

                {/* Behavior */}
                <div className={styles.walletDetailBlock}>
                  <h5 className={styles.walletDetailBlockTitle}>Historical Behavior</h5>
                  <div className={styles.behaviorDetailGrid}>
                    <div className={styles.behaviorDetailStat}>
                      <span className={styles.behaviorDetailLabel}>Avg Sell Time</span>
                      <span className={styles.behaviorDetailValue} style={{ color: '#F56565' }}>3.2 hours</span>
                    </div>
                    <div className={styles.behaviorDetailStat}>
                      <span className={styles.behaviorDetailLabel}>Panic Sells</span>
                      <span className={styles.behaviorDetailValue}>8/10</span>
                    </div>
                    <div className={styles.behaviorDetailStat}>
                      <span className={styles.behaviorDetailLabel}>Buy Back Rate</span>
                      <span className={styles.behaviorDetailValue}>23%</span>
                    </div>
                    <div className={styles.behaviorDetailStat}>
                      <span className={styles.behaviorDetailLabel}>Avg Loss</span>
                      <span className={styles.behaviorDetailValue} style={{ color: '#F56565' }}>-18%</span>
                    </div>
                  </div>
                </div>

                {/* Risk Assessment */}
                <div className={styles.walletDetailRisk}>
                  <AlertTriangle size={14} style={{ color: '#F56565' }} />
                  <span className={styles.walletDetailRiskText}>
                    High cascade risk - triggers selling in 12+ wallets
                  </span>
                </div>
              </>
            ) : (
              <div className={styles.walletDetailEmpty}>
                <Users size={24} style={{ color: 'rgba(115, 246, 255, 0.3)' }} />
                <span>Select a wallet to view details</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainLayout}>
        {/* Single Asset Card */}
        <div className={styles.assetSection}>
          <div className={styles.assetCard}>
            <div className={styles.assetCardHeader}>
              <span className={styles.assetSymbol}>{newAsset.symbol}</span>
              <span className={styles.newBadge}>TESTING</span>
              <span className={styles.assetName}>{newAsset.name}</span>
            </div>
            
            {/* Core Metrics */}
            <div className={styles.metricsGrid}>
              <div className={styles.metricItem}>
                <Users className={styles.metricIcon} />
                <div className={styles.metricContent}>
                  <span className={styles.metricValue}>
                    {newAsset.holders > 1000000 ? `${(newAsset.holders / 1000000).toFixed(1)}M` : `${(newAsset.holders / 1000).toFixed(0)}K`}
                  </span>
                  <span className={styles.metricLabel}>Holders</span>
                </div>
              </div>
              
              <div className={styles.metricItem}>
                <BarChart3 className={styles.metricIcon} />
                <div className={styles.metricContent}>
                  <span className={styles.metricValue}>{newAsset.whale_pct}%</span>
                  <span className={styles.metricLabel}>Whale %</span>
                </div>
              </div>
              
              <div className={styles.metricItem}>
                <TrendingUp className={styles.metricIcon} />
                <div className={styles.metricContent}>
                  <span className={`${styles.metricValue} ${newAsset.holder_growth_7d > 0 ? styles.textGreen : styles.textRed}`}>
                    {newAsset.holder_growth_7d > 0 ? '+' : ''}{newAsset.holder_growth_7d}%
                  </span>
                  <span className={styles.metricLabel}>7d Growth</span>
                </div>
              </div>
            </div>

            {/* Liquidity Metrics */}
            <div className={styles.liquidityRow}>
              <div className={styles.liquidityItem}>
                <Droplet className={styles.liquidityIcon} />
                <span className={styles.liquidityLabel}>Liquidity</span>
                <span className={styles.liquidityValue}>
                  ${newAsset.dex_liquidity > 1000000 ? `${(newAsset.dex_liquidity/1000000).toFixed(1)}M` : `${(newAsset.dex_liquidity/1000).toFixed(0)}K`}
                </span>
              </div>
              <div className={styles.liquidityItem}>
                <Users className={styles.liquidityIcon} />
                <span className={styles.liquidityLabel}>24h Traders</span>
                <span className={`${styles.liquidityValue} ${newAsset.dex_traders_24h < 50 ? styles.textRed : newAsset.dex_traders_24h < 500 ? styles.textYellow : styles.textGreen}`}>
                  {newAsset.dex_traders_24h.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Portfolio Overlap */}
            <div className={styles.overlapSection}>
              <div className={styles.sectionTitle}>
                Direct Holder Overlap
                <span className={styles.overlapCount}>89 Wallets</span>
              </div>
              {Object.entries(holderOverlap).map(([asset, percentage]) => (
                <div key={asset} className={styles.overlapItem}>
                  <span className={styles.overlapAsset}>{asset}</span>
                  <div className={styles.overlapBarBg}>
                    <div 
                      className={styles.overlapBarFill} 
                      style={{ 
                        width: `${percentage}%`,
                        background: getOverlapColor(percentage)
                      }}
                    />
                  </div>
                  <span className={styles.overlapPercent} style={{ color: getOverlapColor(percentage) }}>
                    {percentage}%
                  </span>
                  <button 
                    className={styles.overlapDetailBtn}
                    onClick={() => {
                      setActiveModal('overlapWallets');
                      setModalData({ 
                        asset, 
                        percentage, 
                        title: `${asset} Overlap Analysis`,
                        overlapType: 'holder'
                      });
                    }}
                    title="View overlapping wallets"
                  >
                    <Users size={12} />
                  </button>
                </div>
              ))}
              <div className={styles.overlapAvg}>
                <span className={styles.avgLabel}>Average Overlap</span>
                <span className={styles.avgValue} style={{ color: getOverlapColor(assessment.avgOverlap) }}>
                  {assessment.avgOverlap.toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Financial Exposure Carousel */}
            <div className={styles.exposureRow}>
              <div className={styles.assetIndicator}>
                <span className={styles.assetIndicatorSymbol}>{currentAsset.symbol}</span>
                <div className={styles.assetIndicatorDots}>
                  {portfolioAssets.map((_, index) => (
                    <div 
                      key={index} 
                      className={`${styles.dot} ${index === currentAssetIndex ? styles.dotActive : ''}`}
                    />
                  ))}
                </div>
              </div>
              <div className={styles.exposureDivider}></div>
              <div className={styles.exposureItem}>
                <span className={styles.exposureLabel}>Total USD Exposure</span>
                <span className={styles.exposureValue}>${currentExposure.totalUSD}M</span>
              </div>
              <div className={styles.exposureDivider}></div>
              <div className={styles.exposureItem}>
                <span className={styles.exposureLabel}>Avg Position</span>
                <span className={styles.exposureValue}>${currentExposure.avgPosition}M</span>
              </div>
              <div className={styles.exposureDivider}></div>
              <div className={styles.exposureItem}>
                <span className={styles.exposureLabel}>Supply Control</span>
                <span className={styles.exposureValue} style={{ 
                  color: currentExposure.supplyControl > 0.6 ? '#F59E0B' : '#10b981' 
                }}>
                  {currentExposure.supplyControl}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Section */}
        <div className={styles.analysisSection}>
          {/* Enhanced AI Summary with Impact Metrics - Always Expanded */}
          <div className={`${styles.aiSummary} ${getSeverityStyle(assessment.severity)}`}>
            <div className={styles.aiHeader}>
              {getSeverityIcon(assessment.severity)}
              <span className={styles.aiTitle}>Portfolio Impact Analysis</span>
            </div>
            
            {/* Inline Impact Metrics - WITH CORRELATION */}
            <div className={styles.inlineMetrics}>
              <button 
                className={`${styles.inlineMetricButton} ${activeMetricReport === 'volatility' ? styles.inlineMetricButtonActive : ''} ${
                  impactMetrics.volatility_change < -10 ? styles.metricButtonGreen :
                  impactMetrics.volatility_change < 0 ? styles.metricButtonLightGreen :
                  impactMetrics.volatility_change < 10 ? styles.metricButtonYellow :
                  styles.metricButtonRed
                }`}
                onClick={() => setActiveMetricReport(activeMetricReport === 'volatility' ? null : 'volatility')}
              >
                <span className={styles.inlineLabel}>Volatility</span>
                <span className={styles.inlineValue}>
                  {impactMetrics.volatility_change > 0 ? '+' : ''}{impactMetrics.volatility_change}%
                </span>
              </button>
              <button 
                className={`${styles.inlineMetricButton} ${activeMetricReport === 'sharpe' ? styles.inlineMetricButtonActive : ''} ${
                  impactMetrics.sharpe_change > 0.15 ? styles.metricButtonGreen :
                  impactMetrics.sharpe_change > 0 ? styles.metricButtonLightGreen :
                  impactMetrics.sharpe_change > -0.1 ? styles.metricButtonYellow :
                  styles.metricButtonRed
                }`}
                onClick={() => setActiveMetricReport(activeMetricReport === 'sharpe' ? null : 'sharpe')}
              >
                <span className={styles.inlineLabel}>Sharpe</span>
                <span className={styles.inlineValue}>
                  {impactMetrics.sharpe_change > 0 ? '+' : ''}{impactMetrics.sharpe_change}
                </span>
              </button>
              <button 
                className={`${styles.inlineMetricButton} ${activeMetricReport === 'diversification' ? styles.inlineMetricButtonActive : ''} ${
                  impactMetrics.diversification_ratio > 1.5 ? styles.metricButtonGreen :
                  impactMetrics.diversification_ratio > 1.2 ? styles.metricButtonLightGreen :
                  impactMetrics.diversification_ratio > 1 ? styles.metricButtonYellow :
                  styles.metricButtonRed
                }`}
                onClick={() => setActiveMetricReport(activeMetricReport === 'diversification' ? null : 'diversification')}
              >
                <span className={styles.inlineLabel}>Diversification</span>
                <span className={styles.inlineValue}>{impactMetrics.diversification_ratio}x</span>
              </button>
              <button 
                className={`${styles.inlineMetricButton} ${activeMetricReport === 'correlation' ? styles.inlineMetricButtonActive : ''} ${
                  assessment.avgCorrelation < 0.2 ? styles.metricButtonGreen :
                  assessment.avgCorrelation < 0.4 ? styles.metricButtonLightGreen :
                  assessment.avgCorrelation < 0.7 ? styles.metricButtonYellow :
                  styles.metricButtonRed
                }`}
                onClick={() => setActiveMetricReport(activeMetricReport === 'correlation' ? null : 'correlation')}
              >
                <span className={styles.inlineLabel}>Weighted Correlation</span>
                <span className={styles.inlineValue}>
                  {assessment.avgCorrelation.toFixed(2)}
                </span>
              </button>
            </div>
            
            {/* Metric Report Display */}
            {activeMetricReport && (
              <div className={styles.metricReport}>
                <div className={styles.metricReportContent}>
                  {activeMetricReport === 'volatility' && (
                    <p className={styles.metricReportText}>
                      Adding {newAsset.symbol} reduces portfolio volatility by {Math.abs(impactMetrics.volatility_change)}%, 
                      primarily due to its low correlation with existing holdings. Historical analysis shows this asset 
                      maintains stability during market turbulence, with only {newAsset.volatility_30d}% 30-day volatility.
                    </p>
                  )}
                  {activeMetricReport === 'sharpe' && (
                    <p className={styles.metricReportText}>
                      Sharpe ratio improvement of +{impactMetrics.sharpe_change} indicates enhanced risk-adjusted returns. 
                      This asset's low volatility profile ({newAsset.volatility_30d}%) combined with steady performance 
                      optimizes the portfolio's efficiency frontier.
                    </p>
                  )}
                  {activeMetricReport === 'diversification' && (
                    <p className={styles.metricReportText}>
                      Diversification ratio of {impactMetrics.diversification_ratio}x indicates strong portfolio balance. 
                      With only {assessment.avgOverlap.toFixed(0)}% holder overlap, you're accessing genuinely different 
                      market participants and risk factors.
                    </p>
                  )}
                  {activeMetricReport === 'correlation' && (
                    <p className={styles.metricReportText}>
                      Average correlation of {(assessment.avgCorrelation * 100).toFixed(0)}% with your portfolio 
                      ({assessment.avgCrisisCorr < 0 ? 'negative' : 'positive'} {Math.abs(assessment.avgCrisisCorr).toFixed(2)} during crises). 
                      With ${(newAsset.dex_volume_24h).toFixed(0)} daily volume across {newAsset.dex_traders_24h} traders, 
                      {newAsset.dex_traders_24h < 100 ? ' rebalancing may be challenging' : ' liquidity appears adequate'}.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Correlation Matrix */}
          <div className={styles.correlationMatrix}>
            <div className={styles.matrixHeader}>
              <h3 className={styles.matrixTitle}>Asset Correlations</h3>
              <div className={styles.matrixTabs}>
                <button 
                  className={`${styles.matrixTab} ${activeCorrelationTab === 'normal' ? styles.matrixTabActive : ''}`}
                  onClick={() => setActiveCorrelationTab('normal')}
                >
                  30 Day
                </button>
                <button 
                  className={`${styles.matrixTab} ${activeCorrelationTab === 'rally' ? styles.matrixTabActive : ''}`}
                  onClick={() => setActiveCorrelationTab('rally')}
                >
                  Rally
                </button>
                <button 
                  className={`${styles.matrixTab} ${activeCorrelationTab === 'crisis' ? styles.matrixTabActive : ''}`}
                  onClick={() => setActiveCorrelationTab('crisis')}
                >
                  Crisis
                </button>
              </div>
            </div>
            
            <div className={styles.matrixContent}>
              <div className={styles.matrixGrid}>
                {/* Header Row */}
                <div className={styles.matrixCorner}></div>
                <div className={styles.matrixHeaderCell}>BNB</div>
                <div className={styles.matrixHeaderCell}>ETH</div>
                <div className={styles.matrixHeaderCell}>BTC</div>
                <div className={styles.matrixHeaderCell}>LINK</div>
                
                {/* BNB Row */}
                <div className={styles.matrixRowHeader}>BNB</div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${styles.matrixSelf}`}>1.00</span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixLow : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixNegative
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.12' : 
                     activeCorrelationTab === 'rally' ? '0.85' : '-0.05'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixLow : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixNegative
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.08' : 
                     activeCorrelationTab === 'rally' ? '0.78' : '-0.08'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixLow : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixLow
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.15' : 
                     activeCorrelationTab === 'rally' ? '0.72' : '0.02'}
                  </span>
                </div>
                
                {/* ETH Row */}
                <div className={styles.matrixRowHeader}>ETH</div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixLow : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixNegative
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.12' : 
                     activeCorrelationTab === 'rally' ? '0.85' : '-0.05'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${styles.matrixSelf}`}>1.00</span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixMedium : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixHigh
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.68' : 
                     activeCorrelationTab === 'rally' ? '0.92' : '0.82'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixMedium : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixHigh
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.45' : 
                     activeCorrelationTab === 'rally' ? '0.88' : '0.71'}
                  </span>
                </div>
                
                {/* BTC Row */}
                <div className={styles.matrixRowHeader}>BTC</div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixLow : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixNegative
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.08' : 
                     activeCorrelationTab === 'rally' ? '0.78' : '-0.08'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixMedium : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixHigh
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.68' : 
                     activeCorrelationTab === 'rally' ? '0.92' : '0.82'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${styles.matrixSelf}`}>1.00</span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixMedium : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixMedium
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.38' : 
                     activeCorrelationTab === 'rally' ? '0.85' : '0.65'}
                  </span>
                </div>
                
                {/* LINK Row */}
                <div className={styles.matrixRowHeader}>LINK</div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixLow : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixLow
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.15' : 
                     activeCorrelationTab === 'rally' ? '0.72' : '0.02'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixMedium : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixHigh
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.45' : 
                     activeCorrelationTab === 'rally' ? '0.88' : '0.71'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${
                    activeCorrelationTab === 'normal' ? styles.matrixMedium : 
                    activeCorrelationTab === 'rally' ? styles.matrixHigh :
                    styles.matrixMedium
                  }`}>
                    {activeCorrelationTab === 'normal' ? '0.38' : 
                     activeCorrelationTab === 'rally' ? '0.85' : '0.65'}
                  </span>
                </div>
                <div className={styles.matrixCell}>
                  <span className={`${styles.matrixValue} ${styles.matrixSelf}`}>1.00</span>
                </div>
              </div>
              
              {/* Legend */}
              <div className={styles.matrixLegend}>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendColor} ${styles.legendGreen}`}></span>
                  Low (0-0.3)
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendColor} ${styles.legendYellow}`}></span>
                  Medium (0.3-0.7)
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendColor} ${styles.legendRed}`}></span>
                  High (0.7+)
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendColor} ${styles.legendPurple}`}></span>
                  Negative
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Overlay */}
      {activeModal && (
        <div className={styles.modalOverlay} onClick={() => setActiveModal(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{modalData?.title || modalData?.wallet}</h3>
              <button className={styles.modalClose} onClick={() => setActiveModal(null)}>×</button>
            </div>
            <div className={styles.modalContent}>
              {activeModal === 'overlapWallets' ? (
                <OverlapWalletsModal />
              ) : activeModal === 'walletDetail' ? (
                <WalletDetailModal />
              ) : (
                <p>Detailed analysis for {modalData?.type || modalData?.wallet} would appear here.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioImpactComponent;
